// Candados del menú — módulos contratables que se VEN aunque no estén activos.
//
// Decisión de producto (escalamiento, 2026-09): un módulo que se contrata
// (add-on por sede, o feature de un plan superior) NO se oculta del menú: se
// muestra con un candado y, al entrar, la propia ruta presenta la pantalla
// vendedora (LoyaltyAddonInvite para add-ons, <PlanGate>/LockedFeatureCard
// para features de plan). El candado es SOLO una señal visual: quien hace
// cumplir el gate es el backend (403 ADDON_REQUIRED / PLAN_FEATURE_LOCKED).
//
// Lo único que sigue OCULTANDO un ítem es:
//   - el permiso RBAC (accessId) — igual que siempre, y
//   - que el módulo no esté desplegado (kill-switch del backend, p.ej.
//     LOYALTY_ENABLED=false): mostrarlo llevaría a rutas que responden 404.

/** Add-ons contratables que tienen un ítem propio en el menú. */
export type MenuAddonKey = "fidelizacion";

/** Propiedades opcionales que un ítem de menú puede declarar para el candado. */
export interface MenuLockSpec {
  /** El ítem es un add-on por sede (tipo de ADDON_TYPES del backend). */
  addon?: MenuAddonKey;
  /** El ítem es una feature de plan (clave de FEATURE_GATES / <PlanGate>). */
  planFeature?: string;
}

export interface MenuAddonState {
  /** Módulo desplegado (kill-switch). `null` = aún no se sabe. */
  available: boolean | null;
  /** La sede tiene el add-on vigente (o está exenta). `null` = aún no se sabe. */
  active: boolean | null;
}

export interface MenuLockState {
  addons: Partial<Record<MenuAddonKey, MenuAddonState>>;
  /** Mapa feature→incluida del plan del local; `null` = sin información. */
  planFeatures: Record<string, boolean> | null;
}

export const EMPTY_MENU_LOCK_STATE: MenuLockState = {
  addons: {},
  planFeatures: null,
};

/**
 * ¿Se oculta el ítem por falta de despliegue? Solo aplica a add-ons: mientras
 * el kill-switch no confirme `true` el ítem no aparece (nunca un enlace roto,
 * mismo criterio que tenía el menú con `requiresLoyalty`).
 */
export const isMenuItemHidden = (
  item: MenuLockSpec,
  state: MenuLockState
): boolean => {
  if (!item.addon) return false;
  return state.addons[item.addon]?.available !== true;
};

/**
 * ¿Se muestra el candado? Solo con información CONFIRMADA de que falta: ante
 * la duda (cargando, endpoint caído) no se pone candado — la pantalla de
 * destino resuelve igual con su propio gate.
 */
export const isMenuItemLocked = (
  item: MenuLockSpec,
  state: MenuLockState
): boolean => {
  if (item.addon) {
    const addon = state.addons[item.addon];
    if (addon?.available === true && addon.active === false) return true;
  }
  if (item.planFeature && state.planFeatures) {
    const { planFeatures } = state;
    if (item.planFeature in planFeatures && !planFeatures[item.planFeature]) {
      return true;
    }
  }
  return false;
};

type MenuItemLike = MenuLockSpec & { accessId?: string };

/**
 * Filtra y anota las secciones del menú de perfil: quita lo que el rol no
 * puede ver o lo que no está desplegado, marca `locked` en lo contratable que
 * falta y descarta secciones que quedan vacías.
 */
export const resolveMenuSections = <TSection extends { items: MenuItemLike[] }>(
  sections: TSection[],
  state: MenuLockState,
  hasAccess: (accessId: string) => boolean
): Array<
  Omit<TSection, "items"> & {
    items: Array<TSection["items"][number] & { locked: boolean }>;
  }
> =>
  sections
    .map((section) => ({
      ...section,
      items: section.items
        .filter(
          (item) =>
            (!item.accessId || hasAccess(item.accessId)) &&
            !isMenuItemHidden(item, state)
        )
        .map((item) => ({ ...item, locked: isMenuItemLocked(item, state) })),
    }))
    .filter((section) => section.items.length > 0);
